import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useHistory } from 'react-router';
import { makeStyles } from 'tss-react/mui';

import { Chip } from '@mui/material';

import useStores from 'hooks/use-stores';
import { SearchType } from 'main/components/SearchBar';
import { ElasticQueryBuilder } from 'main/modules/elastic';
import parseJson from 'utils/parse-json';

const useStyles = makeStyles()({
    root: {
        margin: '40px auto',
        width: '100%',
        marginBottom: 16
    },
    chips: {
        display: 'flex',
        justifyContent: 'flex-start',
        flexWrap: 'wrap',
        paddingBottom: 30,
        '@media (max-width: 960px)': {
            display: 'flex',
            flexDirection: 'row',
            overflowX: 'scroll',
            flexWrap: 'nowrap',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
                display: 'none'
            }
        }
    },
    chip: {
        height: 37,
        marginTop: 8,
        marginRight: 8,
        fontSize: 12,
        borderRadius: 100,
        background: '#fff',
        boxShadow: '0px 5px 25px rgba(92, 106, 126, 0.1);',
        '&:hover': {
            background: '#fafafa'
        },
        '&:focus': {
            background: '#fafafa'
        }
    },
    label: {
        padding: '10px 4px 7px 3px'
    },
    link: {
        textDecoration: 'none',
        color: 'initial'
    }
});

const MAX_TEMPLATES = 11;

interface Template {
    id: string;
    type: string;
    published: string;
    modified: string;
    query: string;
    elasticQuery: string;
    alias: string;
    order: number;
    scope: string[];
}

interface Props {
    classes?: {
        root?: string;
        chip?: string;
        chips?: string;
    };
    forceShowAll?: boolean;
    chipOutlined?: boolean;
    onClick?: (template: Template, event: React.MouseEvent) => void;
    forceSearchType?: SearchType;
}

const SearchTemplates: React.FC<Props> = ({ classes, forceShowAll, forceSearchType, chipOutlined, onClick }) => {
    const { classes: internalClasses, cx } = useStyles();
    const history = useHistory();
    const [showAll, toggleShowAll] = useReducer((state) => !state, false);

    const { searchStore, sTemplatesStore } = useStores();
    const searchType = forceSearchType || searchStore.type;

    useEffect(() => {
        sTemplatesStore.templates.length === 0 && sTemplatesStore.loadData();
    }, [sTemplatesStore]);

    const onChipClick = useCallback(
        (item) => (event: React.MouseEvent) => {
            onClick?.(item, event);
            if (event.defaultPrevented) return;

            if (searchType === SearchType.elastic) {
                const state = parseJson(item.elasticQuery);
                searchStore.setParameters({
                    query: state.searchTerm ?? '',
                    type: SearchType.elastic,
                    software: '',
                    version: '',
                    state
                });
                searchStore.performFetch();
                history.push(`/search?state=${ElasticQueryBuilder.stateToString(state)}`);
            } else {
                searchStore.setParameters({
                    query: item.query,
                    type: SearchType.simple,
                    software: '',
                    version: '',
                    state: {}
                });
                searchStore.doRequest({ query: item.query });
                history.push(`/search?query=${item.query}`);
            }
        },
        [history, onClick, searchStore, searchType]
    );

    const prevent = useCallback((event) => event.preventDefault(), []);

    const templates = useMemo(
        () => (showAll || forceShowAll ? sTemplatesStore.templates : sTemplatesStore.templates.slice(0, MAX_TEMPLATES)),
        [forceShowAll, sTemplatesStore.templates, showAll]
    );

    if (sTemplatesStore.templates.length === 0) {
        return null;
    }

    return (
        <div className={cx(internalClasses.root, classes?.root)}>
            <div className={cx(internalClasses.chips, classes?.chips)}>
                {templates.map((item) => (
                    <a
                        key={item.alias}
                        href={
                            searchType === SearchType.elastic
                                ? `/search?state=${item.elasticQuery}`
                                : `/search?query=${item.query}`
                        }
                        className={internalClasses.link}
                        onClick={prevent}
                    >
                        <Chip
                            key={item.alias}
                            variant={chipOutlined ? 'outlined' : 'filled'}
                            className={cx(internalClasses.chip, classes?.chip)}
                            label={<span className={internalClasses.label}>{item.alias}</span>}
                            onClick={onChipClick(item)}
                        />
                    </a>
                ))}

                {forceShowAll ? null : (
                    <Chip
                        key="chip-all"
                        className={cx(internalClasses.chip, classes?.chip)}
                        label={<span className={internalClasses.label}>{showAll ? 'hide' : 'show all'}</span>}
                        onClick={toggleShowAll}
                    />
                )}
            </div>
        </div>
    );
};

export default observer(SearchTemplates);
